<template><markdown-wrapper><h1 id="breadcrumbs">Breadcrumbs <a class="header-anchor" href="#breadcrumbs">&#128279;</a></h1>
<blockquote>
<p>Indicate the current page's location within a navigational hierarchy.</p>
</blockquote>
<p><ins class="adsbygoogle"
     style="display:block; text-align:center;"
     data-ad-layout="in-article"
     data-ad-format="fluid"
     data-ad-client="ca-pub-4714899946256166"
     data-ad-slot="4603582855"></ins></p>
<h2 id="example">Example <a class="header-anchor" href="#example">&#128279;</a></h2>
<p>Use <code v-pre>items</code> array to create a breadcrumbs nav. <code v-pre>active</code> state of last element is automatically set if it is undefined.</p>
<breadcrumbs-example ref="breadcrumbs-example"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">breadcrumbs</span> <span class="hljs-attr">:items</span>=<span class="hljs-string">&quot;items&quot;</span>/&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">items</span>: [
          {<span class="hljs-attr">text</span>: <span class="hljs-string">&#x27;Home&#x27;</span>, <span class="hljs-attr">href</span>: <span class="hljs-string">&#x27;#&#x27;</span>},
          {<span class="hljs-attr">text</span>: <span class="hljs-string">&#x27;Library&#x27;</span>, <span class="hljs-attr">href</span>: <span class="hljs-string">&#x27;#&#x27;</span>},
          {<span class="hljs-attr">text</span>: <span class="hljs-string">&#x27;Data&#x27;</span>, <span class="hljs-attr">href</span>: <span class="hljs-string">&#x27;#&#x27;</span>}
        ]
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- breadcrumbs-example.vue --&gt;</span>
</code></pre>
<h2 id="breadcrumb-item">Breadcrumb item <a class="header-anchor" href="#breadcrumb-item">&#128279;</a></h2>
<p>You can also use <code v-pre>&lt;breadcrumb-item&gt;</code> in breadcrumbs directly. This is useful while full control of item text is need (e.g. HTML tags).</p>
<p>Note that <code v-pre>active</code> state will not automatically set if using this mode.</p>
<breadcrumbs-item ref="breadcrumbs-item"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">breadcrumbs</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">breadcrumb-item</span> <span class="hljs-attr">href</span>=<span class="hljs-string">&quot;#&quot;</span>&gt;</span><span class="hljs-tag">&lt;<span class="hljs-name">b</span>&gt;</span>Home<span class="hljs-tag">&lt;/<span class="hljs-name">b</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">breadcrumb-item</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">breadcrumb-item</span> <span class="hljs-attr">href</span>=<span class="hljs-string">&quot;#&quot;</span>&gt;</span>Library<span class="hljs-tag">&lt;/<span class="hljs-name">breadcrumb-item</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">breadcrumb-item</span> <span class="hljs-attr">active</span>&gt;</span>Data<span class="hljs-tag">&lt;/<span class="hljs-name">breadcrumb-item</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">breadcrumbs</span>&gt;</span>
<span class="hljs-comment">&lt;!-- breadcrumbs-item.vue --&gt;</span>
</code></pre>
<h2 id="router-link">Router link <a class="header-anchor" href="#router-link">&#128279;</a></h2>
<p>Parse <code v-pre>to</code> (String or Object) instead of <code v-pre>href</code> will create a <code v-pre>router-link</code> for the breadcrumb item, which you can use with <a href="https://router.vuejs.org/">Vue-Router</a>.</p>
<breadcrumbs-router-link ref="breadcrumbs-router-link"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">breadcrumbs</span> <span class="hljs-attr">:items</span>=<span class="hljs-string">&quot;items&quot;</span>/&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">items</span>: [
          {<span class="hljs-attr">text</span>: <span class="hljs-string">&#x27;Home&#x27;</span>, <span class="hljs-attr">to</span>: <span class="hljs-string">&#x27;/&#x27;</span>, <span class="hljs-attr">exact</span>: <span class="hljs-literal">true</span>},
          {<span class="hljs-attr">text</span>: <span class="hljs-string">&#x27;Breadcrumbs&#x27;</span>, <span class="hljs-attr">to</span>: <span class="hljs-string">&#x27;/breadcrumbs&#x27;</span>}
        ]
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- breadcrumbs-router-link.vue --&gt;</span>
</code></pre>
<h1 id="api-reference">API Reference <a class="header-anchor" href="#api-reference">&#128279;</a></h1>
<h2 id="breadcrumbs-1"><a href="https://github.com/wxsms/uiv/blob/0.x/src/components/breadcrumbs/Breadcrumbs.js">Breadcrumbs</a> <a class="header-anchor" href="#breadcrumbs-1">&#128279;</a></h2>
<h3 id="props">Props <a class="header-anchor" href="#props">&#128279;</a></h3>
<div class="table-responsive"><table class="table table-bordered table-hover"><thead>
<tr>
<th>Name</th>
<th>Type</th>
<th>Default</th>
<th>Required</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>items</code></td>
<td>Array</td>
<td></td>
<td></td>
<td>Breadcrumb items to create. Props defined in each item object is the same with BreadcrumbItem.vue, except <code v-pre>text</code> will be replace as the breadcrumb item body.</td>
</tr>
</tbody>
</table></div><h3 id="slots">Slots <a class="header-anchor" href="#slots">&#128279;</a></h3>
<div class="table-responsive"><table class="table table-bordered table-hover"><thead>
<tr>
<th>Name</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>default</code></td>
<td>The breadcrumbs body.</td>
</tr>
</tbody>
</table></div><h2 id="breadcrumbitem"><a href="https://github.com/wxsms/uiv/blob/0.x/src/components/breadcrumbs/BreadcrumbItem.js">BreadcrumbItem</a> <a class="header-anchor" href="#breadcrumbitem">&#128279;</a></h2>
<h3 id="props-1">Props <a class="header-anchor" href="#props-1">&#128279;</a></h3>
<div class="table-responsive"><table class="table table-bordered table-hover"><thead>
<tr>
<th>Name</th>
<th>Type</th>
<th>Default</th>
<th>Required</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>active</code></td>
<td>Boolean</td>
<td>false</td>
<td></td>
<td>Set item to active state.</td>
</tr>
<tr>
<td><code v-pre>href</code></td>
<td>String</td>
<td></td>
<td></td>
<td>An native link will be created if this prop present.</td>
</tr>
<tr>
<td><code v-pre>target</code></td>
<td>String</td>
<td></td>
<td></td>
<td>Native link prop.</td>
</tr>
<tr>
<td><code v-pre>to</code></td>
<td>String or Object</td>
<td></td>
<td></td>
<td>An Vue-Router link will be created if this prop present.</td>
</tr>
<tr>
<td><code v-pre>replace</code></td>
<td>Boolean</td>
<td>false</td>
<td></td>
<td>Vue-Router link prop.</td>
</tr>
<tr>
<td><code v-pre>append</code></td>
<td>Boolean</td>
<td>false</td>
<td></td>
<td>Vue-Router link prop.</td>
</tr>
<tr>
<td><code v-pre>exact</code></td>
<td>Boolean</td>
<td>false</td>
<td></td>
<td>Vue-Router link prop.</td>
</tr>
</tbody>
</table></div><h3 id="slots-1">Slots <a class="header-anchor" href="#slots-1">&#128279;</a></h3>
<div class="table-responsive"><table class="table table-bordered table-hover"><thead>
<tr>
<th>Name</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>default</code></td>
<td>The breadcrumb item body.</td>
</tr>
</tbody>
</table></div></markdown-wrapper></template><script>
  

  
export default {components:{'breadcrumbs-example':{"template":"<div class=\"markdown-live-example\">\n  <breadcrumbs :items=\"items\"/>\n</div>",
    data () {
      return {
        items: [
          {text: 'Home', href: '#'},
          {text: 'Library', href: '#'},
          {text: 'Data', href: '#'}
        ]
      }
    }
  },'breadcrumbs-item':{"template":"<div class=\"markdown-live-example\"><breadcrumbs>\n  <breadcrumb-item href=\"#\"><b>Home</b></breadcrumb-item>\n  <breadcrumb-item href=\"#\">Library</breadcrumb-item>\n  <breadcrumb-item active>Data</breadcrumb-item>\n</breadcrumbs>\n<!-- breadcrumbs-item.vue --></div>"},'breadcrumbs-router-link':{"template":"<div class=\"markdown-live-example\">\n  <breadcrumbs :items=\"items\"/>\n</div>",
    data () {
      return {
        items: [
          {text: 'Home', to: '/', exact: true},
          {text: 'Breadcrumbs', to: '/breadcrumbs'}
        ]
      }
    }
  }}}</script>